<template>
  <div class="status">
    <div class="status__container">
      <div class="status__content">
        <template v-if="!loading">
          <div class="status__heading-wrapper">
            <p-content-title :title="`${$t('paribu.name')} status`" :social-icons="[]" />

            <PButton button-type="ghost" status="success" @on-click="onFollowClick">
              <span>{{ $t('status.followUpdates') }}</span>
              <PIcon icon="notification-bell-alarm" color="black" class="f-utility-16" />
            </PButton>
          </div>

          <div v-if="0 < statusData?.active_incidents.length" class="mt-64">
            <status-highlight-item
              v-for="(incident, index) in statusData.active_incidents"
              :key="`active-${incident.id}`"
              :color="getColorFromStatus(incident.status)"
              :heading="incident.name"
              :messages="
                incident.incident_updates.map((update) => ({
                  status: getLabelFromStatus(update.status),
                  message: update.body,
                  date: formatStatusDate(update.created_at),
                }))
              "
              :link="appLink(['status', incident.id])"
              :divider="index < statusData.active_incidents.length - 1"
            />
          </div>

          <div v-if="statusData?.components?.groups?.length" class="mt-64">
            <status-item
              v-for="(group, index) in statusData.components.groups"
              :key="`group-${group.name}`"
              :name="group.name"
              :status="getLabelFromStatus(getOverallStatus(group.components))"
              :color="getColorFromComponentStatus(getOverallStatus(group.components))"
              :items="
                group.components.map((c) => ({
                  name: c.name,
                  status: getLabelFromStatus(c.status),
                  color: getColorFromComponentStatus(c.status),
                }))
              "
              :divider="index < statusData.components.groups.length - 1"
            />
          </div>

          <div class="mt-64">
            <p-page-subtitle :title="$t('status.pastIncidents')" class="mb-32" />

            <div
              v-for="(incident, index) in visibleHistoricalIncidents"
              :key="`past-${incident.id}`"
            >
              <div class="f-utility-16-medium t-fg-tertiary mb-8">
                {{
                  new Date(incident.created_at).toLocaleDateString('tr-TR', { dateStyle: 'long' })
                }}
              </div>

              <div v-if="incident.incident_updates.length">
                <status-highlight-item
                  :color="getColorFromStatus(incident.status)"
                  :heading="incident.name"
                  :messages="
                    incident.incident_updates.map((update) => ({
                      status: getLabelFromStatus(update.status),
                      message: update.body,
                      date: formatStatusDate(update.created_at),
                    }))
                  "
                  :link="appLink(['status', incident.id])"
                  :divider="index < statusData.historical_incidents.length - 1"
                />
              </div>

              <div v-else class="f-utility-14 t-fg-tertiary">
                {{ $t('status.noIncidentsWereReportedToday') }}
              </div>
            </div>
          </div>

          <PModal
            :modal-visibility="isFollowModalVisible"
            class="status__modal"
            @on-close="onCloseFollowModal"
          >
            <template #title>
              <div class="status__modal-title-with-close">
                <span>
                  {{
                    formSuccess ? $t('status.subscribe.successTitle') : $t('status.subscribe.title')
                  }}
                </span>
                <PButton
                  button-type="ghost"
                  size="sm"
                  class="justify-content-end"
                  @click="onCloseFollowModal"
                >
                  <PIcon icon="close" size="sm" color="fg-secondary" />
                </PButton>
              </div>
            </template>

            <template v-if="formSuccess" #description>
              <p class="t-fg-secondary f-utility-14 d-flex align-items-center">
                <PIcon icon="circle-check" size="sm" color="fg-accent" class="mr-8" />
                {{ $t('status.subscribe.successMessage') }}
              </p>
            </template>

            <template v-else #description>
              <p class="t-fg-secondary f-utility-14">
                {{ $t('status.subscribe.description') }}
              </p>
            </template>

            <template v-if="!formSuccess">
              <div class="email-subscribe-form">
                <PTextInput
                  v-model="subscriberEmail"
                  name="subscriberEmail"
                  :placeholder="$t('status.subscribe.inputPlaceholder')"
                />
                <p v-if="emailError" class="t-fg-danger f-utility-12 mt-2 position-absolute">
                  {{ emailError }}
                </p>

                <PButton
                  class="full-width mt-24"
                  button-type="filled"
                  status="success"
                  :loading="subscribeLoading"
                  @on-click="submitEmail"
                >
                  {{ $t('status.subscribe.button') }}
                </PButton>
              </div>
            </template>
          </PModal>
        </template>
      </div>
    </div>
  </div>

  <div ref="sentinel" class="scroll-sentinel" />
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useAppLink } from '@/composables/useAppLink'
import { useStatus, useSubscribeToStatus } from '@/api/useStatus'

const HISTORICAL_INCIDENT_BATCH = 5

const { t, locale } = useI18n()
const language = locale.value.split('-')[0]
const { appLink } = useAppLink()
const { statusData, loading, error } = useStatus()
const {
  subscribe,
  loading: subscribeLoading,
  error: subscribeError,
  success,
} = useSubscribeToStatus()
const isFollowModalVisible = ref(false)
const subscriberEmail = ref('')
const emailError = ref('')
const formSuccess = ref(false)
const historicalIncidentLimit = ref(HISTORICAL_INCIDENT_BATCH)
const sentinel = ref<HTMLElement | null>(null)

const visibleHistoricalIncidents = computed(() => {
  return statusData?.value?.historical_incidents.slice(0, historicalIncidentLimit.value) || []
})

const getColorFromStatus = (status: string): string => {
  switch (status) {
    case 'resolved':
      return 'success'
    case 'investigating':
      return 'accent'
    case 'monitoring':
      return 'warning'
    case 'under_maintenance':
    case 'maintenance':
      return 'blue'
    case 'major':
    case 'critical':
      return 'danger'
    default:
      return 'neutral'
  }
}

const getColorFromComponentStatus = (status: string): string => {
  switch (status) {
    case 'operational':
      return 'accent'
    case 'degraded_performance':
      return 'warning'
    case 'partial_outage':
      return 'accent'
    case 'major_outage':
      return 'danger'
    case 'under_maintenance':
    case 'maintenance':
      return 'blue'
    default:
      return 'neutral'
  }
}

const getOverallStatus = (components: { status: string }[]): string => {
  const statuses = components.map((c) => c.status)

  if (statuses.includes('major_outage')) return 'major_outage'
  if (statuses.includes('partial_outage')) return 'partial_outage'
  if (statuses.includes('degraded_performance')) return 'degraded_performance'
  if (statuses.includes('under_maintenance') || statuses.includes('maintenance')) {
    return 'maintenance'
  }
  return 'operational'
}

const getLabelFromStatus = (status: string): string => {
  return t(`status.incident.${status}`)
}

const formatStatusDate = (date: string) => {
  return new Date(date)
    .toLocaleString(locale.value || 'en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/Istanbul',
      timeZoneName: 'shortOffset',
    })
    .replace(/(\w+) (\d{2}), (\d{4}), (\d{2}):(\d{2}) (GMT[+-]\d{2}:\d{2})/, '$1 $2, $3 - $4:$5 $6')
}

const onFollowClick = () => {
  isFollowModalVisible.value = true
}

const onCloseFollowModal = () => {
  isFollowModalVisible.value = false
  subscriberEmail.value = ''
  emailError.value = ''
  formSuccess.value = false
}

const validateEmailField = (): boolean => {
  const value = subscriberEmail.value.trim()
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  if (!value) {
    emailError.value = {
      tr: 'E-posta boş bırakılamaz.',
      en: 'Email cannot be empty.',
    }[language]
    return false
  }

  if (!emailRegex.test(value)) {
    emailError.value = {
      tr: 'Geçerli bir e-posta adresi giriniz.',
      en: 'Please enter a valid email address.',
    }[language]
    return false
  }

  emailError.value = ''
  return true
}

const submitEmail = async () => {
  if (!validateEmailField()) return

  await subscribe(subscriberEmail.value)

  if (subscribeError.value) {
    emailError.value = t('errors.server.internal_error')
    return
  }

  formSuccess.value = true
  subscriberEmail.value = ''
}

onMounted(() => {
  if (!sentinel.value) return

  const observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries
      if (entry.isIntersecting) {
        if (
          visibleHistoricalIncidents.value.length <
          (statusData.value?.historical_incidents?.length || 0)
        ) {
          historicalIncidentLimit.value += HISTORICAL_INCIDENT_BATCH
        }
      }
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    },
  )

  observer.observe(sentinel.value)
})

definePageMeta({
  name: 'status',
})
</script>
